<template>
  <div class="mark-info m-i-point" v-show="visible">
    <!-- 标题栏 -->
    <div class="m-i-title">
      <div class="txt">标注信息</div>
      <div class="close-icon" @click="handleClose">
        <CloseOutlined />
      </div>
    </div>

    <!-- 名称和操作 -->
    <div class="m-i-name">
      <div class="icon">
        <MarkIcons type="point" color="#2d8cf0"/>
      </div>
      <div class="name">
        <a-input
          v-model:value="current.localName"
          size="small"
          :maxlength="28"
          placeholder="请输入标注名称"
          @change="handleNameChange"
        />
      </div>
      <div class="view" @click="handleView">
        <AimOutlined />
      </div>
      <a-popconfirm title="是否确定删除当前图形?" okText="确定" okType="danger" @confirm="handleDelete">
      <div class="del">
        <DeleteOutlined />
      </div>
      </a-popconfirm>
    </div>

    <a-divider />

    <!-- 测量数据 -->
    <div class="m-i-measure-txt">
      测量数据
      <ExclamationCircleOutlined />
    </div>

    <div class="m-i-measure-info">
      <!-- 经纬度模式 -->
      <div class="m-i-li">
        <div class="key">经纬度模式</div>
        <div class="val">
          <a-radio-group v-model:value="current.coordinateMode" @change="handleModeChange">
            <a-radio value="decimal">十进制</a-radio>
            <a-radio value="dms">度分秒</a-radio>
          </a-radio-group>
        </div>
      </div>

      <!-- 经度 -->
      <div class="m-i-li">
        <div class="key">经度</div>
        <div v-if="current.coordinateMode !== 'dms'" class="val">
          <a-input
            v-model:value="current.longitude"
            size="small"
            @blur="handleLonLatChange"
          />°
        </div>
        <div v-else class="val">
          <a-input
            v-model:value="current.dmsLongitude.degree"
            size="small"
            :maxlength="3"
            @blur="handleDmsLonLatChange"
          />°
          <a-input
            v-model:value="current.dmsLongitude.minute"
            size="small"
            :maxlength="2"
            @blur="handleDmsLonLatChange"
          />′
          <a-input
            v-model:value="current.dmsLongitude.second"
            size="small"
            :maxlength="8"
            @blur="handleDmsLonLatChange"
          />″
        </div>
      </div>

      <!-- 纬度 -->
      <div class="m-i-li">
        <div class="key">纬度</div>
        <div v-if="current.coordinateMode !== 'dms'" class="val">
          <a-input
            v-model:value="current.latitude"
            size="small"
            @blur="handleLonLatChange"
          />°
        </div>
        <div v-else class="val">
          <a-input
            v-model:value="current.dmsLatitude.degree"
            size="small"
            :maxlength="3"
            @blur="handleDmsLonLatChange"
          />°
          <a-input
            v-model:value="current.dmsLatitude.minute"
            size="small"
            :maxlength="2"
            @blur="handleDmsLonLatChange"
          />′
          <a-input
            v-model:value="current.dmsLatitude.second"
            size="small"
            :maxlength="8"
            @blur="handleDmsLonLatChange"
          />″
        </div>
      </div>

      <!-- 椭球高度 -->
      <div class="m-i-li">
        <div class="key">椭球高度</div>
        <div class="val">
          <a-input
            v-model:value="current.altitude"
            size="small"
            @change="handleHeightChange"
          />m
        </div>
      </div>

      <!-- 绘制者 -->
      <div class="m-i-li">
        <div class="key">绘制者</div>
        <div class="val">{{ current.creator }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, toRefs } from 'vue'
import MarkIcons from '/@/components/mark-info/icons.vue'
import { useMyStore } from '/@/store'
import * as Cesium from 'cesium'
import { decimalToDms, dmsToDecimal } from '/@/utils/coordinate'

// eslint-disable-next-line no-undef
const props = defineProps<{
  visible: boolean;
  markList: any[];
  row:any;
  drawTool:any;
  viewer:any

}>()

// eslint-disable-next-line no-undef
const emits = defineEmits(['update:visible', 'delete-mark', 'updateMarkList', 'updateCurrent'])
const store = useMyStore()
const { viewer, visible, row: current, markList, drawTool } = toRefs(props)

const handleView = () => {
  if (current.value.coordinateMode === 'dms') {
    const lon = dmsToDecimal({
      degree: parseFloat(current.value.dmsLongitude.degree),
      minute: parseFloat(current.value.dmsLongitude.minute),
      second: parseFloat(current.value.dmsLongitude.second)
    })
    const lat = dmsToDecimal({
      degree: parseFloat(current.value.dmsLatitude.degree),
      minute: parseFloat(current.value.dmsLatitude.minute),
      second: parseFloat(current.value.dmsLatitude.second)
    })
    viewer.value?.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(
        lon,
        lat,
        parseFloat(current.value.altitude) + 100
      ),
      duration: 1.5
    })
  } else {
    viewer.value?.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(
        parseFloat(current.value.longitude),
        parseFloat(current.value.latitude),
        parseFloat(current.value.altitude) + 100
      ),
      duration: 1.5
    })
  }
}
const handleClose = () => {
  emits('update:visible', false)
  drawTool.value.restoreCameraSettings()
}
const handleDelete = () => {
  emits('delete-mark', current.value)
}
// 辅助函数：查找当前项（支持父子层级）
const findCurrentItem = (list, id) => {
  const item = list.find(el => el.id === id)
  if (item) return { item, isChild: false }
  for (let i = 0; i < list.length; i++) {
    const child = list[i].childItems?.find(e => e.id === id)
    if (child) return { item: child, isChild: true, parentIndex: i, childIndex: list[i].childItems.findIndex(e => e.id === id) }
  }
  return null
}

const handleNameChange = () => {
  const result = findCurrentItem(markList.value, current.value.id)
  if (!result) return
  const { item: row, isChild } = result
  row.iconName = current.value.localName
  row.localName = current.value.localName
  row.coordinateMode = current.value.coordinateMode
  if (isChild) {
    markList.value[result.parentIndex].childItems[result.childIndex] = row
  } else {
    const index = markList.value.findIndex(el => el.id === current.value.id)
    markList.value.splice(index, 1, row)
  }
  emits('updateCurrent', row)
  emits('updateMarkList', markList.value)
}

const handleModeChange = (val) => {
  const result = findCurrentItem(markList.value, current.value.id)
  if (!result) return
  const { item: row, isChild } = result
  row.coordinateMode = val.target.value
  row.iconName = current.value.localName
  row.localName = current.value.localName
  if (isChild) {
    markList.value[result.parentIndex].childItems[result.childIndex] = row
  } else {
    const index = markList.value.findIndex(el => el.id === current.value.id)
    markList.value.splice(index, 1, row)
  }
  emits('updateCurrent', row)
  emits('updateMarkList', markList.value)
}

const handleLonLatChange = () => {
  nextTick(() => {
    const entity = drawTool.value?.getEntityById(current.value.id)?.entity as any
    const result = findCurrentItem(markList.value, current.value.id)
    if (!result) return
    const { item: point, isChild } = result
    point.longitude = parseFloat(current.value.longitude)
    point.latitude = parseFloat(current.value.latitude)
    point.altitude = parseFloat(current.value.altitude)
    point.coordinateMode = current.value.coordinateMode
    point.iconName = current.value.localName
    point.localName = current.value.localName
    if (isChild) {
      markList.value[result.parentIndex].childItems[result.childIndex] = point
    } else {
      const index = markList.value.findIndex(el => el.id === current.value.id)
      markList.value.splice(index, 1, point)
    }
    emits('updateCurrent', point)
    emits('updateMarkList', markList.value)
    drawTool.value?.updatePositionById(current.value.id, Cesium.Cartesian3.fromDegrees(parseFloat(current.value.longitude), parseFloat(current.value.latitude), parseFloat(current.value.altitude)))
    drawTool.value.restoreCameraSettings()
    if (entity) {
      const longitude = parseFloat(current.value.longitude)
      const latitude = parseFloat(current.value.latitude)
      const altitude = parseFloat(current.value.altitude)
      entity.position = Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude)
    }
  })
}

const handleDmsLonLatChange = () => {
  nextTick(() => {
    const entity = drawTool.value?.getEntityById(current.value.id)?.entity as any
    const lon = dmsToDecimal({
      degree: parseFloat(current.value.dmsLongitude.degree),
      minute: parseFloat(current.value.dmsLongitude.minute),
      second: parseFloat(current.value.dmsLongitude.second)
    })
    const lat = dmsToDecimal({
      degree: parseFloat(current.value.dmsLatitude.degree),
      minute: parseFloat(current.value.dmsLatitude.minute),
      second: parseFloat(current.value.dmsLatitude.second)
    })
    const altitude = parseFloat(current.value.altitude)
    const result = findCurrentItem(markList.value, current.value.id)
    if (!result) return
    const { item: point, isChild } = result
    point.longitude = parseFloat(lon)
    point.latitude = parseFloat(lat)
    point.altitude = parseFloat(altitude)
    point.coordinateMode = current.value.coordinateMode
    point.iconName = current.value.localName
    point.localName = current.value.localName
    if (isChild) {
      markList.value[result.parentIndex].childItems[result.childIndex] = point
    } else {
      const index = markList.value.findIndex(el => el.id === current.value.id)
      markList.value.splice(index, 1, point)
    }
    emits('updateCurrent', point)
    emits('updateMarkList', markList.value)
    drawTool.value?.updatePositionById(
      current.value.id, Cesium.Cartesian3.fromDegrees(parseFloat(lon),
        parseFloat(lat),
        parseFloat(altitude)))
    drawTool.value.restoreCameraSettings()
    if (entity) {
      entity.position = Cesium.Cartesian3.fromDegrees(lon, lat, altitude)
    }
  })
}

const handleHeightChange = () => {
  if (current.value.coordinateMode === 'dms') {
    handleDmsLonLatChange()
  } else {
    handleLonLatChange()
  }
}

</script>

<style scoped lang="scss">
.mark-info {
  position: absolute;
  left: 330px;
  top: 10%;
  z-index: 301;
  background: #ffff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(255,255,255, 0.85);
  padding: 16px;
  width: 260px;
}

.m-i-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .txt {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0,0,0, 0.85);
  }

  .close-icon {
    cursor: pointer;
    color: rgba(0,0,0, 0.45);
    transition: color 0.3s;

    &:hover {
      color: rgba(0,0,0, 0.25);
    }
  }
}

.m-i-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;

  .icon {
    flex-shrink: 0;
  }

  .name {
    flex: 1;

    :deep(.ant-input) {
      font-size: 12px;
    }
  }

  .view, .del {
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.3s;

    &:hover {
      background-color: rgba(0,0,0, 0.36);
    }
  }

  .view {
    color: #2d8cf0;
  }

  .del {
    color: #ff4d4f;
  }
}

.m-i-color {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .txt {
    font-size: 14px;
    color: rgba(0,0,0, 0.15);
  }

  .color {
    display: flex;
    gap: 8px;

    .c-li {
      width: 20px;
      height: 20px;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;

      &:hover {
        transform: scale(1.1);
      }
    }
  }
}

.m-i-measure-txt {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;

  .anticon-exclamation-circle {
    color: #faad14;
  }
}

.m-i-measure-info {
  .m-i-li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    .key {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.65);
      flex-shrink: 0;
    }

    .val {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.85);
      display: flex;
      align-items: center;
      gap: 4px;

      :deep(.ant-input) {
        font-size: 12px;
      }
    }
  }
}

:deep(.ant-divider) {
  margin: 14px -16px;
  min-width: auto;
}
</style>
